import { useEffect, useMemo } from "preact/hooks";
import type { ComponentChildren } from "preact";
import { ACCESS_LABELS, FAC_LABELS, LAYERS, PERIOD_COLORS, DISCIPLINE_LABELS, dishLabel, disciplineLabel, kindLabel, mealLabel, periodLabel } from "../data/layers";
import type { EquinePoint, Feature, FoodPoint, GaaPoint, NaturePoint, Parking, SessionPoint, Site, Source, TrainPoint, Walking } from "../types";
import { allEvents, focusOn, mapFocus, eventVenueName, eventsOpen, nearby, saved, select, selectedVenue, toggleSaved, type EventVenueMarker } from "../state/store";
import { fmtDateRange, fmtKm, humanize } from "../utils/format";
import { EventRow, EventsHere } from "./Events";
import { FEEDS } from "../data/events";

function Tag({ label, color }: { label: string; color?: string }) {
  return <span class="tag" style={color ? { "--tag": color } : undefined}>{label}</span>;
}
function Row({ label, children, wide }: { label: string; children: ComponentChildren; wide?: boolean }) {
  if (children == null || children === "" || children === false) return null;
  return <div class={wide ? "span-2" : ""}><dt>{label}</dt><dd>{children}</dd></div>;
}
function Ext({ href, children, cls }: { href: string; children: ComponentChildren; cls?: string }) {
  return <a href={href} target="_blank" rel="noopener" class={cls}>{children}</a>;
}

function parkingText(p?: Parking): string {
  if (!p) return "";
  const bits: string[] = [];
  if (p.verify) bits.push("⚠ location needs checking");
  if (p.type) bits.push(humanize(p.type));
  if (p.free === true) bits.push("free"); else if (p.free === false) bits.push("paid");
  if (p.notes) bits.push(`— ${p.notes}`);
  return bits.join(", ");
}
function walkingText(w?: Walking): string {
  if (!w) return "";
  const bits: string[] = [];
  if (w.distance_m) bits.push(`${w.distance_m} m`);
  if (w.time_min) bits.push(`~${w.time_min} min`);
  if (w.difficulty) bits.push(w.difficulty);
  if (w.terrain) bits.push(humanize(w.terrain));
  if (w.livestock_present || w.cattle_present) bits.push("livestock in field");
  if (w.stile_or_gate) bits.push("stile/gate");
  if (w.exposure === "exposed") bits.push("exposed");
  if (w.notes) bits.push(`— ${w.notes}`);
  return bits.join(", ");
}

function Photo({ f }: { f: Feature }) {
  const photo = (f.raw as Site).photo;
  if (!photo?.url || !photo.credit) return null;
  const credit = `Photo: ${photo.credit}${photo.license ? `, ${photo.license}` : ""}`;
  return (
    <figure class="hero">
      <img src={photo.url} alt={`Photograph of ${f.name}`} loading="lazy" decoding="async" width={photo.width} height={photo.height} />
      <figcaption>{photo.source_url ? <Ext href={photo.source_url}>{credit}</Ext> : credit}</figcaption>
    </figure>
  );
}

function Directions({ f, parking }: { f: { lat: number; lon: number }; parking?: Parking }) {
  const osm = `https://www.openstreetmap.org/?mlat=${f.lat}&mlon=${f.lon}#map=16/${f.lat}/${f.lon}`;
  return (
    <div class="links">
      <Ext href={`https://www.google.com/maps/dir/?api=1&destination=${f.lat},${f.lon}`}>Google Maps</Ext>
      <Ext href={`https://maps.apple.com/?daddr=${f.lat},${f.lon}&dirflg=d`}>Apple Maps</Ext>
      <Ext href={osm}>OpenStreetMap</Ext>
      {parking && typeof parking.lat === "number" && typeof parking.lon === "number" && (
        <>
          <Ext href={`https://www.google.com/maps/dir/?api=1&destination=${parking.lat},${parking.lon}`} cls="strong">Drive to parking</Ext>
          <Ext href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${parking.lat},${parking.lon}`}>Street View at parking</Ext>
        </>
      )}
    </div>
  );
}

function Sources({ sources }: { sources?: Source[] }) {
  if (!sources?.length) return null;
  return (
    <details class="sources">
      <summary>Sources ({sources.length})</summary>
      <ul>{sources.map((s) => <li><Ext href={s.url}>{s.title || s.publisher || s.url}</Ext>{s.publisher && s.title ? <span class="muted"> — {s.publisher}</span> : null}{s.kind ? <span class="muted">, {s.kind}</span> : null}</li>)}</ul>
    </details>
  );
}

function Confidence({ p }: { p: { confidence?: string; last_checked?: string } }) {
  if (!p.confidence && !p.last_checked) return null;
  return (
    <p class={`confidence conf-${p.confidence ?? "medium"}`}>
      {p.confidence === "low" ? "Low confidence — verify before you drive: " : p.confidence === "medium" ? "Medium confidence. " : ""}
      {p.last_checked ? `Last checked ${p.last_checked}.` : ""}
    </p>
  );
}

/** Search links that exist for every history site, no curation required. */
function ResearchLinks({ s }: { s: Site }) {
  const name = s.general_name_en || s.general_name;
  const enc = encodeURIComponent(name);
  const megalith = s.types.some((t) => /tomb|cairn|stone|circle|henge|cursus|ogham|fort|cashel|crannog/.test(t));
  const isNI = /antrim|down|armagh|derry|tyrone|fermanagh/.test(s._region ?? "");
  return (
    <ul class="research">
      <li><Ext href={`https://www.duchas.ie/en/cbes/stories?SearchText=${enc}`}>Dúchas — Schools' Folklore Collection</Ext><span class="muted">, what local children wrote about this place in 1937–38</span></li>
      <li><Ext href={`https://www.logainm.ie/en/s?txt=${encodeURIComponent(s.general_name)}`}>Logainm — placename origins</Ext></li>
      {!isNI && <li><Ext href={`https://excavations.ie/?s=${enc}`}>Excavations.ie — dig reports</Ext></li>}
      {megalith && <li><Ext href={`https://www.megalithic.co.uk/search.php?query=${enc}`}>Megalithic Portal — visitor notes & photos</Ext></li>}
      <li><Ext href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${s.lat},${s.lon}`}>Street View at the site</Ext></li>
    </ul>
  );
}

function HistoryBody({ f }: { f: Feature }) {
  const s = f.raw as Site;
  const fac = s.facilities ? Object.keys(s.facilities).filter((k) => s.facilities![k] && k !== "admission_fee") : [];
  return (
    <>
      <div class="tags">
        {s.periods.map((p) => <Tag label={periodLabel(p)} color={PERIOD_COLORS[p]} />)}
        {s.types.map((t) => <Tag label={humanize(t)} />)}
      </div>
      {s.description && <p class="lede">{s.description}</p>}
      <dl class="grid">
        <Row label="Date range">{fmtDateRange(s.date_start, s.date_end) || "—"}</Row>
        <Row label="Effort">{ACCESS_LABELS[s.accessibility ?? ""] ?? s.accessibility}</Row>
        <Row label="Facilities">{FAC_LABELS[s.facilities_level ?? ""] ?? s.facilities_level}</Row>
        <Row label="Entry">{s.facilities?.admission_fee === true ? "Admission fee" : s.facilities?.admission_fee === false ? "Free" : null}</Row>
        <Row label="Managed by">{s.managed_by}</Row>
        <Row label="Hours">{s.access_hours}</Row>
        <Row label="Parking" wide>{parkingText(s.parking)}</Row>
        <Row label="Walk in" wide>{walkingText(s.walking)}</Row>
        <Row label="Without a car" wide>{s.public_transport}</Row>
      </dl>
      {fac.length > 0 && <div class="tags">{fac.map((k) => <Tag label={humanize(k)} />)}</div>}
      {s.tips?.length ? <div class="tips"><h4>On the ground</h4><ul>{s.tips.map((t) => <li>{t}</li>)}</ul></div> : null}
      {s.aliases?.length ? (
        <section>
          <h3>Layers of history here</h3>
          <div class="aliases">
            {s.aliases.map((a) => (
              <div class="alias">
                <h4>{a.name || a.name_en || a.name_ga}{a.name_ga && a.name_ga !== a.name && <span class="name-sub">, {a.name_ga}</span>}{a.name_en && a.name_en !== a.name && <span class="name-sub">, {a.name_en}</span>}</h4>
                <div class="alias-meta">{fmtDateRange(a.date_start, a.date_end) || "—"}{a.type ? `, ${humanize(a.type)}` : ""}</div>
                {a.description && <p>{a.description}</p>}
              </div>
            ))}
          </div>
        </section>
      ) : null}
      <section>
        <h3>Read more</h3>
        <div class="links">
          {(s.urls?.official || s.url) && <Ext href={(s.urls?.official || s.url)!}>Official</Ext>}
          {s.urls?.wikipedia && <Ext href={s.urls.wikipedia}>Wikipedia</Ext>}
        </div>
        {s.accounts?.length ? (
          <>
            <h4 class="sub-h">Accounts worth your time</h4>
            <ul class="accounts">
              {s.accounts.map((a) => (
                <li>
                  <Ext href={a.url}>{a.title}</Ext>
                  <div class="muted">{[a.source, a.kind, a.year].filter(Boolean).join(", ")}</div>
                  {a.note && <div class="account-note">{a.note}</div>}
                </li>
              ))}
            </ul>
          </>
        ) : null}
        <h4 class="sub-h">Dig deeper</h4>
        <ResearchLinks s={s} />
      </section>
      {s.coord_audit && <p class="muted small">Pin corrected {s.coord_audit.date} from {s.coord_audit.source} (moved {s.coord_audit.moved_m} m).</p>}
      <section><h3>Directions</h3><Directions f={f} parking={s.parking} /></section>
    </>
  );
}

function NatureBody({ f }: { f: Feature }) {
  const p = f.raw as NaturePoint;
  return (
    <>
      <div class="tags">{p.kinds.map((k) => <Tag label={kindLabel(k)} color={LAYERS.nature.color} />)}</div>
      {p.summary && <p class="lede">{p.summary}</p>}
      {p.description && <p>{p.description}</p>}
      {p.geology && (p.geology.story || p.geology.rock) && (
        <section class="block">
          <h3>The rock</h3>
          {(p.geology.rock || p.geology.age) && <p class="muted">{[p.geology.rock, p.geology.age].filter(Boolean).join(", ")}</p>}
          {p.geology.story && <p>{p.geology.story}</p>}
        </section>
      )}
      {p.ecology && (p.ecology.species?.length || p.ecology.habitats?.length) ? (
        <section class="block">
          <h3>The life</h3>
          {p.ecology.habitats?.length ? <p><b>Habitats:</b> {p.ecology.habitats.join(", ")}</p> : null}
          {p.ecology.species?.length ? <p><b>Look for:</b> {p.ecology.species.join(", ")}</p> : null}
          {p.ecology.best_season && <p><b>Best season:</b> {p.ecology.best_season}</p>}
        </section>
      ) : null}
      <dl class="grid">
        <Row label="Best time">{p.best_time}</Row>
        <Row label="Weather">{p.weather}</Row>
        <Row label="Parking" wide>{parkingText(p.parking) || p.practical?.parking}</Row>
        <Row label="Walk" wide>{walkingText(p.walking)}</Row>
        <Row label="Hours / fee">{[p.practical?.hours, p.practical?.fee].filter(Boolean).join(", ")}</Row>
        <Row label="Without a car" wide>{p.practical?.public_transport}</Row>
        <Row label="Safety" wide>{p.safety}</Row>
        <Row label="Notes" wide>{p.practical?.notes}</Row>
      </dl>
      {p.tips?.length ? <div class="tips"><h4>On the ground</h4><ul>{p.tips.map((t) => <li>{t}</li>)}</ul></div> : null}
      <div class="links">{p.urls?.official && <Ext href={p.urls.official}>Official</Ext>}{p.urls?.wikipedia && <Ext href={p.urls.wikipedia}>Wikipedia</Ext>}</div>
      <Sources sources={p.sources} />
      <Confidence p={p} />
      <section><h3>Directions</h3><Directions f={f} parking={p.parking} /></section>
    </>
  );
}

function EquineBody({ f }: { f: Feature }) {
  const p = f.raw as EquinePoint;
  const r = p.raceday;
  return (
    <>
      <div class="tags">
        {p.kinds.map((k) => <Tag label={kindLabel(k)} color={LAYERS.equine.color} />)}
        {(p.disciplines || []).map((d) => <Tag label={DISCIPLINE_LABELS[d] ?? disciplineLabel(d)} />)}
      </div>
      {p.summary && <p class="lede">{p.summary}</p>}
      {p.description && <p>{p.description}</p>}
      <dl class="grid">
        <Row label="Track">{p.surface}</Row>
        <Row label="Shape" wide>{p.circuit}</Row>
        <Row label="Racing since">{p.founded}</Row>
        <Row label="Capacity">{p.capacity?.toLocaleString()}</Row>
        <Row label="The big days" wide>{p.feature_meetings?.join("; ")}</Row>
        <Row label="Parking" wide>{p.practical?.parking}</Row>
        <Row label="Hours / fee">{[p.practical?.hours, p.practical?.fee].filter(Boolean).join(", ")}</Row>
        <Row label="Without a car" wide>{p.practical?.public_transport}</Row>
        <Row label="Notes" wide>{p.practical?.notes}</Row>
      </dl>
      <EventsHere placeKey={`equine:${p.id}`} title="Fixtures here" />
      {r && (r.season || r.tickets || r.fixtures || r.etiquette || r.where_to_stand) ? (
        <section class="block matchday">
          <h3>Going racing here</h3>
          <dl class="grid">
            <Row label="Season" wide>{r.season}</Row>
            <Row label="Fixtures" wide>{r.fixtures}</Row>
            <Row label="Tickets" wide>{r.tickets}</Row>
            <Row label="Where to stand" wide>{r.where_to_stand}</Row>
            <Row label="What it's like" wide>{r.etiquette}</Row>
          </dl>
        </section>
      ) : null}
      {p.history && <section class="block"><h3>The story</h3><p>{p.history}</p></section>}
      {p.tips?.length ? <div class="tips"><h4>On the ground</h4><ul>{p.tips.map((t) => <li>{t}</li>)}</ul></div> : null}
      <div class="links">{p.urls?.official && <Ext href={p.urls.official}>Official</Ext>}{p.urls?.wikipedia && <Ext href={p.urls.wikipedia}>Wikipedia</Ext>}</div>
      <Sources sources={p.sources} />
      <Confidence p={p} />
      <section><h3>Directions</h3><Directions f={f} /></section>
    </>
  );
}

function GaaBody({ f }: { f: Feature }) {
  const p = f.raw as GaaPoint;
  const m = p.matchday;
  return (
    <>
      <div class="tags">{p.kinds.map((k) => <Tag label={kindLabel(k)} color={LAYERS.gaa.color} />)}{(p.codes || []).map((c) => <Tag label={humanize(c)} />)}</div>
      {p.summary && <p class="lede">{p.summary}</p>}
      {p.description && <p>{p.description}</p>}
      <dl class="grid">
        <Row label="Home of">{p.home}</Row>
        <Row label="Capacity">{p.capacity?.toLocaleString()}</Row>
        <Row label="Parking" wide>{p.practical?.parking}</Row>
        <Row label="Hours / fee">{[p.practical?.hours, p.practical?.fee].filter(Boolean).join(", ")}</Row>
        <Row label="Without a car" wide>{p.practical?.public_transport}</Row>
        <Row label="Notes" wide>{p.practical?.notes}</Row>
      </dl>
      <EventsHere placeKey={`gaa:${p.id}`} title="Matches here" />
      {m && (m.season || m.tickets || m.fixtures || m.etiquette || m.where_to_stand) ? (
        <section class="block matchday">
          <h3>Going to a match</h3>
          <dl class="grid">
            <Row label="Season" wide>{m.season}</Row>
            <Row label="Fixtures" wide>{m.fixtures}</Row>
            <Row label="Tickets" wide>{m.tickets}</Row>
            <Row label="Where to stand" wide>{m.where_to_stand}</Row>
            <Row label="Etiquette" wide>{m.etiquette}</Row>
          </dl>
        </section>
      ) : null}
      {p.history && <section class="block"><h3>The story</h3><p>{p.history}</p></section>}
      {p.tips?.length ? <div class="tips"><h4>On the ground</h4><ul>{p.tips.map((t) => <li>{t}</li>)}</ul></div> : null}
      <div class="links">{p.urls?.official && <Ext href={p.urls.official}>Official</Ext>}{p.urls?.wikipedia && <Ext href={p.urls.wikipedia}>Wikipedia</Ext>}</div>
      <Sources sources={p.sources} />
      <Confidence p={p} />
      <section><h3>Directions</h3><Directions f={f} /></section>
    </>
  );
}

function FoodBody({ f }: { f: Feature }) {
  const p = f.raw as FoodPoint;
  return (
    <>
      <div class="tags">
        {p.kinds.map((k) => <Tag label={kindLabel(k)} color={LAYERS.food.color} />)}
        {p.price && <Tag label={p.price} />}
        {(p.meal || []).map((m) => <Tag label={mealLabel(m)} />)}
      </div>
      {p.coord_precision === "approximate" && (
        <p class="confidence conf-low">⚠ Approximate pin — only the street or townland could be located. Use the address below and phone ahead.</p>
      )}
      {p.summary && <p class="lede">{p.summary}</p>}
      {p.locals_note && <blockquote class="locals">{p.locals_note}</blockquote>}
      {p.dishes?.length ? <div class="tags dishes">{p.dishes.map((d) => <Tag label={dishLabel(d)} color={LAYERS.food.color} />)}</div> : null}
      {p.description && <p>{p.description}</p>}
      <dl class="grid">
        <Row label="Since">{p.since}</Row>
        <Row label="Hours" wide>{p.hours_note || p.practical?.hours}</Row>
        <Row label="Parking" wide>{p.practical?.parking}</Row>
        <Row label="Without a car" wide>{p.practical?.public_transport}</Row>
        <Row label="Notes" wide>{p.practical?.notes}</Row>
      </dl>
      {p.tips?.length ? <div class="tips"><h4>Ordering</h4><ul>{p.tips.map((t) => <li>{t}</li>)}</ul></div> : null}
      <div class="links">{p.urls?.official && <Ext href={p.urls.official}>Website</Ext>}{p.urls?.wikipedia && <Ext href={p.urls.wikipedia}>Wikipedia</Ext>}</div>
      <Sources sources={p.sources} />
      <Confidence p={p} />
      <section><h3>Directions</h3><Directions f={f} /></section>
    </>
  );
}

const OPERATOR_LABELS: Record<string, string> = {
  iarnrod_eireann: "Iarnród Éireann",
  enterprise: "Enterprise (Dublin–Belfast)",
  translink_ni: "Translink NI Railways",
  heritage: "Heritage railway",
};

function TrainBody({ f }: { f: Feature }) {
  const p = f.raw as TrainPoint;
  const isHeritage = p.kinds.includes("heritage_railway");
  return (
    <>
      <div class="tags">
        {p.kinds.map((k) => <Tag label={kindLabel(k)} color={LAYERS.trains.color} />)}
        {(p.lines || []).map((l) => <Tag label={l} />)}
      </div>
      {p.summary && <p class="lede">{p.summary}</p>}
      {p.description && <p>{p.description}</p>}
      <dl class="grid">
        <Row label="Operator">{p.operator ? OPERATOR_LABELS[p.operator] ?? humanize(p.operator) : null}</Row>
        <Row label="Station code">{p.code}</Row>
        <Row label="Parking" wide>{p.practical?.parking}</Row>
        <Row label="Hours / fee" wide>{[p.practical?.hours, p.practical?.fee].filter(Boolean).join(", ")}</Row>
        <Row label="Notes" wide>{p.practical?.notes}</Row>
      </dl>
      {isHeritage && (
        <p class="muted small">Heritage/preserved line — check the operator's site for the current season and timetable before you go.</p>
      )}
      {p.tips?.length ? <div class="tips"><h4>On the ground</h4><ul>{p.tips.map((t) => <li>{t}</li>)}</ul></div> : null}
      <div class="links">{p.urls?.official && <Ext href={p.urls.official}>Official</Ext>}{p.urls?.wikipedia && <Ext href={p.urls.wikipedia}>Wikipedia</Ext>}</div>
      <Sources sources={p.sources} />
      <Confidence p={p} />
      <section><h3>Directions</h3><Directions f={f} /></section>
    </>
  );
}

function SessionBody({ f }: { f: Feature }) {
  const s = f.raw as SessionPoint;
  return (
    <>
      <div class="tags"><Tag label="Trad session" color={LAYERS.sessions.color} /></div>
      <p class="lede">A pub with a regular traditional music session, as listed by musicians on The Session. <b>Which night and what time is on the linked page</b> — sessions move, so check before you go, and arrive early for a seat near the players.</p>
      <dl class="grid">
        <Row label="Town">{s.town}</Row>
        <Row label="County">{s.county}</Row>
        <Row label="Listed">{s.listed}</Row>
      </dl>
      <div class="links"><Ext href={s.url} cls="strong">Session details on The Session</Ext></div>
      <p class="muted small">Data © The Session contributors, ODbL.</p>
      <section><h3>Directions</h3><Directions f={f} /></section>
    </>
  );
}

function Nearby({ f }: { f: Feature }) {
  const groups = useMemo(() => {
    const items = nearby(f.lat, f.lon, 15, f.key);
    const by = new Map<string, { f: Feature; dist: number }[]>();
    for (const it of items) {
      const arr = by.get(it.f.layer) ?? [];
      if (arr.length < 4) arr.push(it);
      by.set(it.f.layer, arr);
    }
    return by;
  }, [f.key]);
  if (!groups.size) return null;
  return (
    <section class="nearby">
      <h3>Within 15 km</h3>
      {[...groups.entries()].map(([layer, items]) => (
        <div class="nearby-group">
          <div class="nearby-head" style={{ "--layer": LAYERS[layer as keyof typeof LAYERS].color }}><span class={`layer-dot shape-${LAYERS[layer as keyof typeof LAYERS].shape}`} />{LAYERS[layer as keyof typeof LAYERS].label}</div>
          <ul>
            {items.map((it) => (
              <li><button type="button" class="btn-link" onClick={() => { select(it.f); focusOn(it.f); }}>{it.f.name}</button><span class="muted">, {fmtKm(it.dist)}</span></li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

export function Detail({ f }: { f: Feature }) {
  const def = LAYERS[f.layer];
  const starred = saved.value.has(f.key);
  useEffect(() => {
    document.getElementById("detail")?.scrollTo({ top: 0 });
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") select(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [f.key]);

  return (
    <div class="detail" id="detail" style={{ "--layer": def.color }}>
      <div class="detail-bar">
        <button type="button" class="btn-back" onClick={() => select(null)}>← Back</button>
        <span class="detail-layer"><span class={`layer-dot shape-${def.shape}`} aria-hidden="true" />{def.label}</span>
        <button type="button" class="btn-star" aria-pressed={starred} aria-label={starred ? "Remove star" : "Star this place"} onClick={() => toggleSaved(f.key)}>{starred ? "★" : "☆"}</button>
      </div>
      <h2>{f.name}</h2>
      {f.sub && <p class="name-sub">{f.sub}</p>}
      <div class="detail-actions">
        <button type="button" class="btn-map" onClick={() => focusOn(f)}>Show on map</button>
        <span class="coords muted">{f.lat.toFixed(5)}, {f.lon.toFixed(5)}</span>
      </div>
      <Photo f={f} />
      {f.layer === "history" && <HistoryBody f={f} />}
      {f.layer === "nature" && <NatureBody f={f} />}
      {f.layer === "gaa" && <GaaBody f={f} />}
      {f.layer === "equine" && <EquineBody f={f} />}
      {f.layer === "food" && <FoodBody f={f} />}
      {f.layer === "sessions" && <SessionBody f={f} />}
      {f.layer === "trains" && <TrainBody f={f} />}
      <Nearby f={f} />
    </div>
  );
}

/** A ringed venue that has no pin of its own — most club grounds, most point-to-point
 *  fields. Same sheet as a feature, but built from the events rather than a curated entry. */
export function VenueDetail({ v }: { v: EventVenueMarker }) {
  const def = LAYERS[FEEDS[v.feed].layer];
  const close = () => { selectedVenue.value = null; };
  useEffect(() => {
    document.getElementById("detail")?.scrollTo({ top: 0 });
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [v.name, v.lat, v.lon]);
  const events = allEvents.value.filter((e) => e.feed === v.feed && e.venue.name === v.name && e.venue.lat === v.lat && e.venue.lon === v.lon);
  const county = v.next.venue.county;
  const word = v.feed === "gaa" ? "match venue" : "fixture venue";
  return (
    <div class="detail" id="detail" style={{ "--layer": def.color }}>
      <div class="detail-bar">
        <button type="button" class="btn-back" onClick={close}>← Back</button>
        <span class="detail-layer"><span class={`layer-dot shape-${def.shape}`} aria-hidden="true" />{def.label} {word}</span>
      </div>
      <h2>{v.name}</h2>
      {county && <p class="name-sub">{county}</p>}
      <div class="detail-actions">
        <button type="button" class="btn-map" onClick={() => { mapFocus.value = { seq: Date.now(), lat: v.lat, lon: v.lon, zoom: 13 }; }}>Show on map</button>
        <span class="coords muted">{v.lat.toFixed(5)}, {v.lon.toFixed(5)}</span>
      </div>
      <p class="lede">
        A venue that is not one of the map's listed places, ringed because there is something on here soon.
        {v.next.venue.approx ? " The ring is on the village, not the field: ask locally or follow the cars." : ""}
      </p>
      <section class="block matches-here">
        <h3>What's on here</h3>
        {events.map((e) => <EventRow e={e} showVenue={false} key={e.key} />)}
        <button type="button" class="btn-link" onClick={() => { eventsOpen.value = true; eventVenueName.value = null; close(); }}>Everything coming up →</button>
      </section>
      <section><h3>Directions</h3><Directions f={v} /></section>
    </div>
  );
}
